import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { getCurrentUser, signIn, signOut, fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import {
  SignOutReason,
  clearActivity,
  clearSignoutReason,
  idleState,
  markActivity,
  readLastActivity,
  setSignoutReason,
  startIdleSession,
} from '../utils/idleSession';

interface UserAttributes {
  email?: string;
  name?: string;
  phone_number?: string;
  'custom:company_id'?: string;
  'custom:company_name'?: string;
  'custom:role'?: string;
  'custom:can_manage_mappings'?: string;
}

interface AuthContextType {
  user: UserAttributes | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Ends the session because of inactivity or an expired session, and tells the login page why. */
  endSession: (reason: SignOutReason) => Promise<void>;
  refreshUser: () => Promise<void>;
  getToken: () => Promise<string>;
  getGroups: () => Promise<string[]>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getGroups = async (): Promise<string[]> => {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken;
  if (!token) return [];
  const payload = token.payload as any;
  return (payload['cognito:groups'] as string[]) || [];
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserAttributes | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Several things can notice an expired session at the same moment (the timer, a
  // request, another tab); only the first one needs to act.
  const endingRef = useRef(false);

  /**
   * Local sign-out. signOut() revokes the refresh token with Cognito when token
   * revocation is on, clears the stored tokens, and never throws. It does NOT
   * sign the user out of their other devices.
   */
  const endSession = async (reason: SignOutReason): Promise<void> => {
    if (endingRef.current) return;
    endingRef.current = true;
    try {
      setSignoutReason(reason);
      clearActivity();
      setUser(null); // hide the data immediately; revocation continues below
      await signOut();
    } catch (error) {
      console.error('Sign-out error:', error);
    } finally {
      endingRef.current = false;
    }
  };

  /**
   * `initial` is true on page load. A session with no recorded activity (one that
   * started before the idle timer existed) or one that has been idle too long is
   * ended instead of resumed.
   */
  const loadUser = async (initial: boolean) => {
    try {
      const currentUser = await getCurrentUser(); // throws when nobody is signed in
      if (currentUser) {
        const last = readLastActivity();
        if (last === null && !initial) {
          // refreshUser() right after a sign-in that did not record activity
          startIdleSession();
        } else if (idleState(last, Date.now()) === 'expired') {
          await endSession(last === null ? 'expired' : 'idle');
          return;
        } else if (initial) {
          markActivity(Date.now(), true); // reopening the app counts as activity
        }
        const attributes = await fetchUserAttributes();
        setUser(attributes);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUser(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const result = await signIn({ username: email, password });
      if (result.isSignedIn) {
        startIdleSession();
        const attributes = await fetchUserAttributes();
        setUser(attributes);
      } else {
        throw new Error('Additional authentication step required: ' + result.nextStep?.signInStep);
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      clearSignoutReason();
      clearActivity();
      await signOut();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const refreshUser = async () => {
    await loadUser(false);
  };

  /**
   * Returns the ID token for an API call. Refuses (and ends the session) when
   * the user has been idle too long or Cognito can no longer refresh the
   * session (the 12-hour refresh-token limit).
   */
  const getToken = async (): Promise<string> => {
    const last = readLastActivity();
    if (idleState(last, Date.now()) === 'expired') {
      await endSession(last === null ? 'expired' : 'idle');
      throw new Error('Session expired');
    }
    let token: string | undefined;
    try {
      const session = await fetchAuthSession();
      token = session.tokens?.idToken?.toString();
    } catch {
      token = undefined;
    }
    if (!token) {
      await endSession('expired');
      throw new Error('Session expired');
    }
    return token;
  };

  // The idle timer lives in <IdleTimeout/>; it reads endSession through this context.
  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        endSession,
        refreshUser,
        getToken,
        getGroups,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
